import { StrictMode, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// Import the generated route tree
import { routeTree } from "./routeTree.gen";

import "./styles.css";
import "./i18n";
import reportWebVitals from "./reportWebVitals.ts";
import { AuthProvider, useAuth } from "./providers/auth.tsx";
import { PostHogProvider } from "posthog-js/react";
import type { PostHogConfig } from "posthog-js";
import * as Sentry from "@sentry/react";
import { OrgProvider, useOrg } from "./providers/organization.tsx";

Sentry.init({
  dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.2,
  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "https://api.inventoree.io",
    "https://app.inventoree.io",
  ],
});

const options: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2026-05-30",
};

export const queryClient = new QueryClient();

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    org: undefined!,
    queryClient,
  },
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultPreloadDelay: 100,
  defaultPendingMs: 100,
  defaultPendingMinMs: 50,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function App() {
  const auth = useAuth();
  const org = useOrg();
  return (
    <>
      <RouterProvider router={router} context={{ auth, org }} />
    </>
  );
}

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      {/* Suspends until the selected language's translations have loaded */}
      <Suspense fallback={null}>
        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
          options={options}
        >
          <QueryClientProvider client={queryClient}>
            <OrgProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </OrgProvider>
          </QueryClientProvider>
        </PostHogProvider>
      </Suspense>
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
